import { useCallback, useEffect, useState } from 'react';
import './painel-fundacao.css';
import { getHealth, getReady, getVersion } from './api';
import type { Fetched, HealthInfo, ReadyInfo, VersionInfo } from './api';

/**
 * Painel de estado da fundação.
 *
 * Existe para provar, na tela, o que a P0 entrega: a API sobe, `/health`
 * responde e `/ready` diz a verdade sobre o PostgreSQL. Não há regra de
 * negócio aqui — os módulos virão nas fases seguintes.
 */

/** Revisão 360: 30 s (era 5 s) e nada com a aba escondida; voltar para a aba relê na hora. */
const REFRESH_MS = 30_000;

function StatusBadge({ tone, children }: { tone: 'ok' | 'bad' | 'wait'; children: string }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="row">
      <span className="row__label">{label}</span>
      <span className="row__value">{children}</span>
    </div>
  );
}

export default function PainelDaFundacao() {
  const [version, setVersion] = useState<Fetched<VersionInfo>>({ state: 'loading' });
  const [health, setHealth] = useState<Fetched<HealthInfo>>({ state: 'loading' });
  const [ready, setReady] = useState<Fetched<ReadyInfo>>({ state: 'loading' });
  const [checkedAt, setCheckedAt] = useState<string>('—');

  const refresh = useCallback(async () => {
    const [nextVersion, nextHealth, nextReady] = await Promise.all([
      getVersion(),
      getHealth(),
      getReady(),
    ]);
    setVersion(nextVersion);
    setHealth(nextHealth);
    setReady(nextReady);
    setCheckedAt(new Date().toLocaleTimeString('pt-BR'));
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => {
      if (!document.hidden) void refresh();
    }, REFRESH_MS);
    const voltou = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', voltou);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', voltou);
    };
  }, [refresh]);

  const databaseStatus =
    ready.state === 'ok'
      ? ready.data.checks.database.status
      : ready.state === 'error'
        ? 'down'
        : null;

  const schemaStatus = ready.state === 'ok' ? ready.data.checks.schema.status : null;

  return (
    <div className="pf">
      <main className="shell">
        <header className="shell__header">
          <h1>InovaPay 2.0</h1>
          <p className="shell__subtitle">Fundação P0 — sem módulo de negócio implantado</p>
        </header>

        <section className="card">
          <h2>Processo</h2>
          <Row label="/health">
            {health.state === 'ok' ? (
              <StatusBadge tone="ok">vivo</StatusBadge>
            ) : health.state === 'loading' ? (
              <StatusBadge tone="wait">verificando</StatusBadge>
            ) : (
              <StatusBadge tone="bad">sem resposta</StatusBadge>
            )}
          </Row>
          <Row label="Uptime">
            {health.state === 'ok' ? `${String(health.data.uptimeSeconds)}s` : '—'}
          </Row>
        </section>

        <section className="card">
          <h2>Prontidão</h2>
          <Row label="/ready">
            {ready.state === 'ok' && ready.data.status === 'ready' ? (
              <StatusBadge tone="ok">pronto</StatusBadge>
            ) : ready.state === 'loading' ? (
              <StatusBadge tone="wait">verificando</StatusBadge>
            ) : (
              <StatusBadge tone="bad">não pronto</StatusBadge>
            )}
          </Row>
          <Row label="PostgreSQL">
            {databaseStatus === 'up' ? (
              <StatusBadge tone="ok">conectado</StatusBadge>
            ) : databaseStatus === 'down' ? (
              <StatusBadge tone="bad">indisponível</StatusBadge>
            ) : (
              <StatusBadge tone="wait">verificando</StatusBadge>
            )}
          </Row>
          <Row label="Schema">
            {schemaStatus === 'ok' ? (
              <StatusBadge tone="ok">íntegro</StatusBadge>
            ) : schemaStatus === 'not_ready' ? (
              <StatusBadge tone="bad">inconsistente</StatusBadge>
            ) : (
              <StatusBadge tone="wait">verificando</StatusBadge>
            )}
          </Row>
          <Row label="Migrations pendentes">
            {ready.state === 'ok' ? String(ready.data.checks.schema.pendingMigrations ?? '—') : '—'}
          </Row>
          <p className="note">
            Banco fora do ar nunca vira estado &ldquo;0&rdquo;: a prontidão responde 503 e diz que o
            sistema não está apto. Migrations pendentes aparece como &ldquo;—&rdquo; quando não é
            possível saber, jamais como zero.
          </p>
        </section>

        <section className="card">
          <h2>Release</h2>
          <Row label="Aplicação">{version.state === 'ok' ? version.data.app : '—'}</Row>
          <Row label="Versão">{version.state === 'ok' ? version.data.version : '—'}</Row>
          <Row label="Ambiente">{version.state === 'ok' ? version.data.environment : '—'}</Row>
          <Row label="Commit">{version.state === 'ok' ? (version.data.commit ?? '—') : '—'}</Row>
          <Row label="Fuso operacional">{version.state === 'ok' ? version.data.timezone : '—'}</Row>
        </section>

        <footer className="shell__footer">
          <span>Última verificação: {checkedAt}</span>
          <button type="button" onClick={() => void refresh()}>
            Verificar agora
          </button>
        </footer>
      </main>
    </div>
  );
}
